/**
 * @file OperationsErrorsPage.jsx
 * @description OPERATIONS department — Errors, as four boxes by origin:
 *              M/C Site Threshold (computed limits), DTC — Vehicle CAN (real
 *              machine fault codes), No Frame Over 4h (machines gone silent),
 *              Device Alerts (TCU reports: power cuts, CAN link, battery).
 *
 *              Clicking an error asks: share it with the SERVICE team? Sharing
 *              records it (ops_raised_errors) and the Service Control Panel
 *              shows "Operations raised" on that exact error. The dialog can
 *              also open the machine's visualization pinned to the error.
 *
 * This file belongs to the OPERATIONS department; its backend
 * (GET /api/service/error-boxes, POST /api/service/ops-raise) is shared.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, Stack, Chip, Skeleton, Alert, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, Snackbar,
} from '@mui/material';
import {
    ReportProblem as ThresholdIcon, Memory as DtcIcon,
    HourglassBottom as SilentIcon, NotificationsActive as AlertIcon,
    Send as ShareIcon, Timeline as VizIcon,
} from '@mui/icons-material';
import serviceDashboardService from '../../../services/serviceDashboardService';
import { formatToIST } from '../../../utils/dataUtils';

const SEV_COLOR = { critical: 'error', warning: 'warning', info: 'default' };

const elapsed = (ts) => {
    if (!ts) return 'never';
    const mins = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 60000));
    const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

const OperationsErrorsPage = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [picked, setPicked] = useState(null);   // {row, source, label} — dialog open
    const [busy, setBusy] = useState(false);
    const [snack, setSnack] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await serviceDashboardService.getErrorBoxes();
            setData(res);
            setError('');
        } catch (e) {
            setError(e?.message || 'Failed to load errors');
            setData({ site_threshold: [], dtcs: [], device_alerts: [], silent_machines: [] });
        }
    }, []);
    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    // The /mewp-detail deep link (same contract as the Daily Report + Service
    // panel): visualization at the error's exact frame.
    const openViz = (row, at, alarmName) => {
        const t = at ? new Date(at) : null;
        if (!t || isNaN(t)) { navigate(`/mewp-detail/${encodeURIComponent(row.equipment_id)}`); return; }
        const day = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        const q = new URLSearchParams({ view: 'viz', date: day, at: t.toISOString() });
        if (alarmName) q.set('alarm', alarmName);
        navigate(`/mewp-detail/${encodeURIComponent(row.equipment_id)}?${q.toString()}`);
    };

    const share = async () => {
        if (!picked) return;
        setBusy(true);
        try {
            const { row, source } = picked;
            const res = await serviceDashboardService.raiseToService({
                equipment_id: row.equipment_id,
                alarm_name: row.alarm_name || 'NO_FRAME',
                source,
                created_at: row.created_at || row.last_seen || null,
            });
            setSnack(res?.already ? 'Already shared with service' : 'Shared with service');
            setPicked(null);
            load();
        } catch (e) {
            setError(e?.message || 'Share failed');
        } finally { setBusy(false); }
    };

    const ErrRow = ({ row, source, label, time, alarmName }) => (
        <Box
            onClick={() => setPicked({ row, source, label, time, alarmName })}
            sx={{
                px: 1.25, py: 1, borderRadius: '10px', cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" fontWeight={800} noWrap>{row.serial_number || row.equipment_id}</Typography>
                {row.severity
                    ? <Chip size="small" label={row.severity} color={SEV_COLOR[row.severity] || 'default'} sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }} />
                    : <Typography variant="caption" color="text.secondary">{elapsed(row.last_seen)}</Typography>}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }} title={row.message || alarmName}>
                {alarmName}{time ? ` · ${formatToIST(time, 'dd MMM, HH:mm')}` : ''}
            </Typography>
            {row.ops_raised_by && (
                <Chip size="small" variant="outlined" color="primary" label="Shared with service"
                    sx={{ mt: 0.5, height: 18, fontSize: 10, fontWeight: 700 }} />
            )}
        </Box>
    );

    const BoxCard = ({ title, icon, rows, source, empty, nameOf, timeOf }) => (
        <Card variant="outlined" sx={{ borderRadius: '16px', p: 2, display: 'flex', flexDirection: 'column', minHeight: 260 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                {icon}
                <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
                <Chip size="small" label={rows.length} sx={{ ml: 'auto', fontWeight: 700 }} />
            </Stack>
            {rows.length === 0 ? (
                <Typography variant="caption" color="text.secondary" fontStyle="italic">{empty}</Typography>
            ) : (
                <Box sx={{ overflowY: 'auto', maxHeight: 300 }}>
                    <Stack spacing={0.25}>
                        {rows.map((r, i) => (
                            <ErrRow key={i} row={r} source={source} label={title}
                                alarmName={nameOf(r)} time={timeOf(r)} />
                        ))}
                    </Stack>
                </Box>
            )}
        </Card>
    );

    const d = data || {};
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {data === null ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={260} />)}
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <BoxCard title="M/C Site Threshold" source="SYSTEM"
                        icon={<ThresholdIcon color="error" sx={{ fontSize: 20 }} />}
                        rows={d.site_threshold || []}
                        empty="No threshold alarms in the last 24 hours."
                        nameOf={(r) => r.alarm_name} timeOf={(r) => r.created_at} />
                    <BoxCard title="DTC — Vehicle CAN" source="DEVICE_DTC"
                        icon={<DtcIcon sx={{ color: '#ea580c', fontSize: 20 }} />}
                        rows={d.dtcs || []}
                        empty="No active fault codes from the CAN."
                        nameOf={(r) => r.alarm_name} timeOf={(r) => r.created_at} />
                    <BoxCard title={`No Frame Over ${d.silence_hours || 4}h`} source="NO_FRAME"
                        icon={<SilentIcon sx={{ color: '#64748b', fontSize: 20 }} />}
                        rows={d.silent_machines || []}
                        empty="Every machine is reporting."
                        nameOf={() => 'No frames received'} timeOf={(r) => r.last_seen} />
                    <BoxCard title="Device Alerts" source="DEVICE_ALERT"
                        icon={<AlertIcon sx={{ color: '#1d4ed8', fontSize: 20 }} />}
                        rows={d.device_alerts || []}
                        empty="No device alerts in the last 24 hours."
                        nameOf={(r) => r.alarm_name} timeOf={(r) => r.created_at} />
                </Box>
            )}

            {/* Share dialog — the click's question. */}
            <Dialog open={!!picked} onClose={() => setPicked(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800 }}>Share with the service team?</DialogTitle>
                <DialogContent>
                    {picked && (
                        <>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>{picked.row.serial_number || picked.row.equipment_id}</strong> — {picked.alarmName}
                                {picked.time ? ` (${formatToIST(picked.time, 'dd MMM yyyy, HH:mm')})` : ''}
                            </Typography>
                            {picked.row.ops_raised_by && (
                                <Alert severity="info" sx={{ mt: 1.5 }} icon={false}>
                                    Already shared by {picked.row.ops_raised_by}.
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPicked(null)}>Cancel</Button>
                    <Button startIcon={<VizIcon />} disabled={busy}
                        onClick={() => { const p = picked; setPicked(null); openViz(p.row, p.time, p.row.alarm_name); }}>
                        Open machine
                    </Button>
                    <Button variant="contained" startIcon={<ShareIcon />} onClick={share}
                        disabled={busy || !!picked?.row?.ops_raised_by}>
                        {busy ? 'Sharing…' : 'Share to service'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}
                message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
};

export default OperationsErrorsPage;
